/**
 * Break Activity Timeline (PR 2, Phase B2)
 * ----------------------------------------
 * Renders the audit trail for a single break row (lifecycle events
 * sourced from `activity_events` where subject_type = "user" and
 * details.break_id == this break's id).
 *
 * Designed to slot into a row-expand area on the Break History,
 * Active Breaks, or Flagged Breaks pages. Compact, vertical timeline
 * with one entry per event.
 *
 * Visual conventions:
 *  - Coloured dot per event type (start/end/reminder/auto/force/ack)
 *  - ET timestamp (matches the rest of the HR module)
 *  - Short summary + optional secondary line (actor, duration, etc.)
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle } from 'lucide-react';
import { formatDateTimeShortET } from './breakUiHelpers';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Dot styling per event type. Falls back to a neutral grey for any
// event we haven't explicitly catalogued (forward-compatible).
const EVENT_META = {
  'break.started':        { dot: 'bg-emerald-500', label: 'Started' },
  'break.ended':          { dot: 'bg-blue-500',    label: 'Ended' },
  'break.auto_ended':     { dot: 'bg-amber-500',   label: 'Auto-Ended' },
  'break.force_ended':    { dot: 'bg-rose-500',    label: 'Force-Ended' },
  'break.acknowledged':   { dot: 'bg-purple-500',  label: 'Acknowledged' },
  'break.reminder_sent':  { dot: 'bg-yellow-500',  label: 'Reminder Sent' },
};

const EventRow = ({ event }) => {
  const meta = EVENT_META[event.event_type] || { dot: 'bg-gray-400', label: event.event_type };
  const details = event.details || {};
  const secondary = [];
  if (event.actor_name) secondary.push(`by ${event.actor_name}`);
  if (details.duration_minutes != null) secondary.push(`${details.duration_minutes} min`);
  if (details.remaining_minutes != null) secondary.push(`${details.remaining_minutes} min remaining`);

  return (
    <div className="flex gap-3 items-start" data-testid={`timeline-event-${event.event_type}`}>
      <div className="flex flex-col items-center pt-1.5">
        <span className={`w-2.5 h-2.5 rounded-full ${meta.dot}`} />
        <span className="w-px flex-1 bg-gray-200 mt-1 min-h-[18px]" />
      </div>
      <div className="flex-1 pb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-gray-700">{meta.label}</span>
          <span className="text-xs text-gray-400">{formatDateTimeShortET(event.created_at)}</span>
        </div>
        {event.summary && (
          <p className="text-sm text-gray-700 mt-0.5">{event.summary}</p>
        )}
        {secondary.length > 0 && (
          <p className="text-xs text-gray-500 mt-0.5">{secondary.join(' · ')}</p>
        )}
      </div>
    </div>
  );
};

const BreakActivityTimeline = ({ breakId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!breakId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await axios.get(`${API}/api/breaks/${breakId}/activity`);
        if (!cancelled) setEvents(res.data?.events || []);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Failed to load timeline');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [breakId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs text-gray-500 py-3" data-testid="timeline-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading activity…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-xs text-rose-600 py-3" data-testid="timeline-error">
        <AlertCircle className="w-4 h-4" />
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="text-xs text-gray-500 py-3" data-testid="timeline-empty">
        No activity recorded for this break yet.
      </div>
    );
  }

  return (
    <div className="pl-2" data-testid="break-activity-timeline">
      {events.map((ev, idx) => (
        <EventRow key={ev.id || idx} event={ev} />
      ))}
    </div>
  );
};

export default BreakActivityTimeline;
